import React, { useState, useEffect } from 'react';
import { useWeb } from '../../context/WebContext';
import { FaSave, FaRedo, FaPlus, FaTrash, FaImage } from 'react-icons/fa';
import toast from 'react-hot-toast';

export default function ManageSettings() {
  const { settings, setSettings, resetAllData } = useWeb();

  // Basic Info Form state
  const [basicInfo, setBasicInfo] = useState({
    companyName: settings?.companyName || '',
    phone1: settings?.phone1 || '',
    phone2: settings?.phone2 || '',
    whatsappNumber: settings?.whatsappNumber || '',
    email: settings?.email || '',
    address: settings?.address || '',
    workingHours: settings?.workingHours || '',
    mapEmbedUrl: settings?.mapEmbedUrl || ''
  });

  // SEO Form state
  const [seoInfo, setSeoInfo] = useState({
    homeTitle: settings?.meta?.home?.title || '',
    homeDesc: settings?.meta?.home?.desc || '',
    aboutTitle: settings?.meta?.about?.title || '',
    aboutDesc: settings?.meta?.about?.desc || '',
    galleryTitle: settings?.meta?.gallery?.title || '',
    galleryDesc: settings?.meta?.gallery?.desc || '',
    contactTitle: settings?.meta?.contact?.title || '',
    contactDesc: settings?.meta?.contact?.desc || '',
  });

  // Hero Slider state
  const [slides, setSlides] = useState([]);

  useEffect(() => {
    if (settings) {
      setBasicInfo({
        companyName: settings.companyName || '',
        phone1: settings.phone1 || '',
        phone2: settings.phone2 || '',
        whatsappNumber: settings.whatsappNumber || '',
        email: settings.email || '',
        address: settings.address || '',
        workingHours: settings.workingHours || '',
        mapEmbedUrl: settings.mapEmbedUrl || ''
      });
      if (settings.meta) {
        setSeoInfo({
          homeTitle: settings.meta.home?.title || '',
          homeDesc: settings.meta.home?.desc || '',
          aboutTitle: settings.meta.about?.title || '',
          aboutDesc: settings.meta.about?.desc || '',
          galleryTitle: settings.meta.gallery?.title || '',
          galleryDesc: settings.meta.gallery?.desc || '',
          contactTitle: settings.meta.contact?.title || '',
          contactDesc: settings.meta.contact?.desc || '',
        });
      }
      if (settings.heroSlides) {
        setSlides(settings.heroSlides);
      }
    }
  }, [settings]);

  const handleSaveBasic = (e) => {
    e.preventDefault();
    setSettings(prev => ({
      ...prev,
      ...basicInfo
    }));
    toast.success('Company contact settings saved!');
  };

  const handleSaveSEO = (e) => {
    e.preventDefault();
    setSettings(prev => ({
      ...prev,
      meta: {
        home: { title: seoInfo.homeTitle, desc: seoInfo.homeDesc },
        about: { title: seoInfo.aboutTitle, desc: seoInfo.aboutDesc },
        gallery: { title: seoInfo.galleryTitle, desc: seoInfo.galleryDesc },
        contact: { title: seoInfo.contactTitle, desc: seoInfo.contactDesc }
      }
    }));
    toast.success('SEO Meta tags configuration saved!');
  };

  const handleAddSlide = () => {
    setSlides(prev => [
      ...prev,
      {
        title: 'New Slide Title',
        subtitle: 'Professional Installation',
        image: 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?auto=format&fit=crop&w=1600&q=80'
      }
    ]);
  };

  const handleDeleteSlide = (index) => {
    setSlides(prev => prev.filter((_, i) => i !== index));
  };

  const handleSlideChange = (index, field, value) => {
    setSlides(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const handleSaveSlides = (e) => {
    e.preventDefault();
    setSettings(prev => ({
      ...prev,
      heroSlides: slides
    }));
    toast.success('Hero slider images saved successfully!');
  };

  return (
    <div className="space-y-10 text-xs sm:text-sm">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Website & SEO Settings</h1>
        <p className="text-slate-500 text-xs sm:text-sm mt-1">Configure company details, business hours, sitemaps, and SEO page meta descriptions.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Company Settings */}
        <div className="bg-white border rounded-2xl p-6 space-y-6">
          <h3 className="text-base font-bold text-slate-900 border-b pb-3">Company Details</h3>
          
          <form onSubmit={handleSaveBasic} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Company Name</label>
              <input 
                type="text" 
                value={basicInfo.companyName}
                onChange={(e) => setBasicInfo({...basicInfo, companyName: e.target.value})}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Helpline Phone 1</label>
                <input 
                  type="text" 
                  value={basicInfo.phone1}
                  onChange={(e) => setBasicInfo({...basicInfo, phone1: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Helpline Phone 2</label>
                <input 
                  type="text" 
                  value={basicInfo.phone2}
                  onChange={(e) => setBasicInfo({...basicInfo, phone2: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">WhatsApp API Number</label>
                <input 
                  type="text" 
                  value={basicInfo.whatsappNumber}
                  onChange={(e) => setBasicInfo({...basicInfo, whatsappNumber: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Business Email</label>
                <input 
                  type="email" 
                  value={basicInfo.email}
                  onChange={(e) => setBasicInfo({...basicInfo, email: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Operational Working Hours</label>
              <input 
                type="text" 
                value={basicInfo.workingHours}
                onChange={(e) => setBasicInfo({...basicInfo, workingHours: e.target.value})}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Corporate Address</label>
              <textarea 
                rows="2" 
                value={basicInfo.address}
                onChange={(e) => setBasicInfo({...basicInfo, address: e.target.value})}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none resize-none"
                required
              ></textarea>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Google Maps Iframe Embed URL</label>
              <textarea 
                rows="2" 
                value={basicInfo.mapEmbedUrl}
                onChange={(e) => setBasicInfo({...basicInfo, mapEmbedUrl: e.target.value})}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none resize-none text-[10px]"
                required
              ></textarea>
            </div>

            <button 
              type="submit"
              className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-2.5 rounded-xl transition flex items-center justify-center space-x-2 shadow"
            >
              <FaSave />
              <span>Save Contact Info</span>
            </button>
          </form>
        </div>

        {/* SEO Settings */}
        <div className="bg-white border rounded-2xl p-6 space-y-6">
          <h3 className="text-base font-bold text-slate-900 border-b pb-3">SEO Configuration (Meta Tags)</h3>
          
          <form onSubmit={handleSaveSEO} className="space-y-4">
            
            {/* Home page SEO */}
            <div className="space-y-2 border-b pb-4">
              <span className="text-xs font-extrabold text-orange-600 uppercase tracking-wider block">Home Page</span>
              <div>
                <label className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">Meta Title</label>
                <input 
                  type="text" 
                  value={seoInfo.homeTitle}
                  onChange={(e) => setSeoInfo({...seoInfo, homeTitle: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg px-3 py-1.5 focus:border-orange-500 outline-none text-xs"
                />
              </div>
              <div>
                <label className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">Meta Description</label>
                <textarea 
                  rows="2" 
                  value={seoInfo.homeDesc}
                  onChange={(e) => setSeoInfo({...seoInfo, homeDesc: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg px-3 py-1.5 focus:border-orange-500 outline-none text-xs resize-none"
                ></textarea>
              </div>
            </div>

            {/* About page SEO */}
            <div className="space-y-2 border-b pb-4">
              <span className="text-xs font-extrabold text-orange-600 uppercase tracking-wider block">About Us Page</span>
              <div>
                <label className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">Meta Title</label>
                <input 
                  type="text" 
                  value={seoInfo.aboutTitle}
                  onChange={(e) => setSeoInfo({...seoInfo, aboutTitle: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg px-3 py-1.5 focus:border-orange-500 outline-none text-xs"
                />
              </div>
              <div>
                <label className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">Meta Description</label>
                <textarea 
                  rows="2" 
                  value={seoInfo.aboutDesc}
                  onChange={(e) => setSeoInfo({...seoInfo, aboutDesc: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg px-3 py-1.5 focus:border-orange-500 outline-none text-xs resize-none"
                ></textarea>
              </div>
            </div>

            {/* Gallery Page SEO */}
            <div className="space-y-2 border-b pb-4">
              <span className="text-xs font-extrabold text-orange-600 uppercase tracking-wider block">Gallery Page</span>
              <div>
                <label className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">Meta Title</label>
                <input 
                  type="text" 
                  value={seoInfo.galleryTitle}
                  onChange={(e) => setSeoInfo({...seoInfo, galleryTitle: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg px-3 py-1.5 focus:border-orange-500 outline-none text-xs"
                />
              </div>
              <div>
                <label className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">Meta Description</label>
                <textarea 
                  rows="2" 
                  value={seoInfo.galleryDesc}
                  onChange={(e) => setSeoInfo({...seoInfo, galleryDesc: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg px-3 py-1.5 focus:border-orange-500 outline-none text-xs resize-none"
                ></textarea>
              </div>
            </div>

            {/* Contact Page SEO */}
            <div className="space-y-2 pb-4">
              <span className="text-xs font-extrabold text-orange-600 uppercase tracking-wider block">Contact Page</span>
              <div>
                <label className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">Meta Title</label>
                <input 
                  type="text" 
                  value={seoInfo.contactTitle}
                  onChange={(e) => setSeoInfo({...seoInfo, contactTitle: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg px-3 py-1.5 focus:border-orange-500 outline-none text-xs"
                />
              </div>
              <div>
                <label className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">Meta Description</label>
                <textarea 
                  rows="2" 
                  value={seoInfo.contactDesc}
                  onChange={(e) => setSeoInfo({...seoInfo, contactDesc: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg px-3 py-1.5 focus:border-orange-500 outline-none text-xs resize-none"
                ></textarea>
              </div>
            </div>

            <button 
              type="submit"
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-2.5 rounded-xl transition flex items-center justify-center space-x-2 shadow"
            >
              <FaSave />
              <span>Save SEO Metadata</span>
            </button>
          </form>
        </div>

      </div>

      {/* Hero Slides Management Section */}
      <div className="bg-white border rounded-2xl p-6 space-y-6">
        <div className="flex justify-between items-center border-b pb-3">
          <h3 className="text-base font-bold text-slate-900 flex items-center space-x-2">
            <FaImage className="text-orange-500" />
            <span>Manage Hero Slider Images</span>
          </h3>
          <button
            type="button"
            onClick={handleAddSlide}
            className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-3 py-1.5 rounded-lg text-xs flex items-center space-x-1 shadow transition"
          >
            <FaPlus />
            <span>Add Slide</span>
          </button>
        </div>

        <form onSubmit={handleSaveSlides} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {slides.map((slide, index) => (
              <div key={index} className="border border-slate-200 rounded-2xl p-4 space-y-4 bg-slate-50/50 relative">
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-extrabold text-xs text-orange-600 uppercase tracking-wider">Slide #{index + 1}</span>
                  <button
                    type="button"
                    onClick={() => handleDeleteSlide(index)}
                    className="text-red-500 hover:text-red-700 p-1 text-xs"
                    title="Delete Slide"
                  >
                    <FaTrash />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 space-y-3">
                    <div>
                      <label className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">Slide Title</label>
                      <input
                        type="text"
                        value={slide.title}
                        onChange={(e) => handleSlideChange(index, 'title', e.target.value)}
                        className="w-full border border-slate-200 rounded-lg px-3 py-1.5 focus:border-orange-500 outline-none text-xs"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">Subtitle / Badge</label>
                      <input
                        type="text"
                        value={slide.subtitle}
                        onChange={(e) => handleSlideChange(index, 'subtitle', e.target.value)}
                        className="w-full border border-slate-200 rounded-lg px-3 py-1.5 focus:border-orange-500 outline-none text-xs"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex flex-col justify-center items-center">
                    <label className="block text-[10px] text-slate-400 font-bold uppercase mb-1 self-start">Preview</label>
                    <div className="w-full h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center">
                      {slide.image ? (
                        <img src={slide.image} alt="Preview" className="w-full h-full object-cover" onError={(e) => {e.target.style.display='none';}} />
                      ) : (
                        <FaImage className="text-slate-300 text-xl" />
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">Slide Image File or URL *</label>
                  <div className="flex flex-col gap-2">
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const data = new FormData();
                        data.append('file', file);
                        const API_URL = import.meta.env.DEV ? 'https://vengamambaenterprises.com/api.php' : '/api.php';
                        try {
                          toast.loading("Uploading image...", { id: 'slide-upload' });
                          const res = await fetch(`${API_URL}?action=upload_image`, {
                            method: 'POST',
                            body: data
                          });
                          const result = await res.json();
                          if (result.success && result.url) {
                            handleSlideChange(index, 'image', result.url);
                            toast.success("Image uploaded successfully!", { id: 'slide-upload' });
                          } else {
                            toast.error(result.error || "Failed to upload image", { id: 'slide-upload' });
                          }
                        } catch (err) {
                          console.error("Upload error:", err);
                          toast.error("Error uploading image", { id: 'slide-upload' });
                        }
                      }}
                      className="w-full border border-slate-200 rounded-lg px-3 py-1 outline-none file:mr-4 file:py-1 file:px-2 file:rounded file:border-0 file:text-[10px] file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 cursor-pointer text-xs"
                    />
                    <input
                      type="text"
                      value={slide.image}
                      onChange={(e) => handleSlideChange(index, 'image', e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-1.5 focus:border-orange-500 outline-none text-xs font-mono"
                      placeholder="https://images.unsplash.com/..."
                      required
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {slides.length === 0 && (
            <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-2xl text-slate-400">
              No hero slides configured. Click "Add Slide" to set up your slider images.
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-2.5 rounded-xl transition flex items-center justify-center space-x-2 shadow"
          >
            <FaSave />
            <span>Save Slider Configuration</span>
          </button>
        </form>
      </div>
    </div>
  );
}
